import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    Link,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { APIType } from '~/backend/types';
import { Modality } from '~/domain/dicom/modality';
import { useBackendModels } from '~/hooks/workflows/backend/useBackend';
import { contourPropagationTaskState } from '~/state/atoms/adapt/contourPropagationTaskAtom';
import { COMPACT_ITEM, COMPACT_SELECT } from './replanningControlSx';
import RoiPlanTable from './RoiPlanTable';
import { defaultRoiPlan, type RoiSource } from './roiPropagationPlan';
import DatasetSelect from './DatasetSelect';
import { PROPAGATION_MODELS } from './propagationModels';
import { usePreselectFirst } from './usePreselectFirst';
import { useComparisonContourModels } from '~/hooks/workflows/backend/tasks/useComparisonContourModel';
import ContourModelSelect from './ContourModelSelect';
import { useComparisonModelChoice } from './useComparisonModelChoice';
import { useInitRigidChoices } from './useInitRigidChoices';
import { usePropagationRunning } from './usePropagationRun';
import { usePlanAndTargetScans } from './usePlanAndTargetScans';
import { useReplanningStructureSets } from './useReplanningStructureSets';
import { useRoiPlan } from './useRoiPlan';

export default function PropagationForm() {
    const intl = useIntl();
    // The run's fixed source set: the ROI colours must not follow the display
    // onto the propagated set once propagation has started.
    const { source } = useReplanningStructureSets();
    const [task, setTask] = useRecoilState(contourPropagationTaskState);
    const { data: registrationModels } = useBackendModels(APIType.REGISTRATION);
    // The QA model draws on the target scan, so its modality decides which models are offered.
    const { target: targetImage } = usePlanAndTargetScans();
    const targetModality = targetImage?.firstSlice.modality ?? Modality.CT;
    const isPropagating = usePropagationRunning();
    const initRigid = useInitRigidChoices(targetImage?.seriesUid);

    const { sourceRois, modelRois, entries } = useRoiPlan();
    const roiColors = useMemo(
        () =>
            new Map(
                source?.roiArray?.map((roi) => [roi.name, roi.color]) ?? []
            ),
        [source]
    );
    const contourModelChoice = useComparisonModelChoice(
        sourceRois,
        targetModality
    );
    const contourModels = useComparisonContourModels(targetModality);
    const builtFor = useRef<string | undefined>();

    useEffect(() => {
        if (sourceRois.length === 0) {
            return;
        }
        // Rebuilt when the catalog arrives: an earlier plan had nothing marked contourable.
        const fresh = defaultRoiPlan(sourceRois, modelRois);
        const signature = JSON.stringify(
            Object.entries(fresh).map(([name, entry]) => [name, entry.source])
        );
        if (builtFor.current === signature) {
            return;
        }
        builtFor.current = signature;
        setTask((current) => ({ ...current, roiPlan: fresh }));
    }, [sourceRois, modelRois, setTask]);

    const setIncluded = (name: string, included: boolean) =>
        setTask((current) => ({
            ...current,
            roiPlan: {
                ...(current.roiPlan ?? {}),
                [name]: {
                    ...current.roiPlan?.[name],
                    included,
                    source: current.roiPlan?.[name]?.source ?? 'propagate',
                },
            },
        }));

    const setSource = (
        name: string,
        source: RoiSource,
        confirmedModelRoiName?: string
    ) =>
        setTask((current) => ({
            ...current,
            roiPlan: {
                ...(current.roiPlan ?? {}),
                [name]: {
                    ...current.roiPlan?.[name],
                    included: current.roiPlan?.[name]?.included ?? true,
                    source,
                    // A confirmation holds only for the model structure it was given for.
                    confirmedModelRoiName:
                        source === 'contour'
                            ? confirmedModelRoiName
                            : undefined,
                },
            },
        }));

    const offered = PROPAGATION_MODELS.filter((entry) =>
        (registrationModels ?? []).some((model) => model.name === entry.name)
    );
    usePreselectFirst({
        options: offered,
        hasSelection: !!task.propagationModelName,
        onSelect: (entry) =>
            setTask((current) =>
                current.propagationModelName
                    ? current
                    : { ...current, propagationModelName: entry.name }
            ),
    });

    return (
        <Box
            display="flex"
            flexDirection="column"
            gap={1}
            height="100%"
            minHeight={0}
        >
            <Typography fontSize={11} color="text.secondary">
                <FormattedMessage id="replanning.propagate.model" />
            </Typography>
            <FormControl size="small" fullWidth>
                <Select
                    displayEmpty
                    disabled={isPropagating}
                    value={task.propagationModelName ?? ''}
                    onChange={(event) =>
                        setTask((current) => ({
                            ...current,
                            propagationModelName: event.target.value,
                        }))
                    }
                    inputProps={{
                        'aria-label': intl.formatMessage({
                            id: 'replanning.propagate.model',
                        }),
                    }}
                    data-testid="replanning-propagation-model"
                    sx={COMPACT_SELECT}
                >
                    {offered.map((entry) => (
                        <MenuItem
                            key={entry.name}
                            value={entry.name}
                            sx={COMPACT_ITEM}
                        >
                            <FormattedMessage id={entry.labelId} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {initRigid.offered && (
                <Box pl={2}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={initRigid.enabled}
                                disabled={isPropagating}
                                onChange={(event) =>
                                    initRigid.setEnabled(event.target.checked)
                                }
                                data-testid="replanning-init-rigid"
                            />
                        }
                        label={
                            <Box fontSize={11}>
                                <FormattedMessage id="replanning.initRigid.use" />
                            </Box>
                        }
                    />
                    {initRigid.enabled && (
                        <Box pl={4}>
                            <DatasetSelect
                                label={intl.formatMessage({
                                    id: 'replanning.initRigid.select',
                                })}
                                options={initRigid.options}
                                selectedUid={initRigid.selectedUid}
                                onSelect={initRigid.select}
                                disabled={isPropagating}
                                testId="replanning-init-rigid-select"
                                emptyLabel={intl.formatMessage({
                                    id: 'replanning.initRigid.none',
                                })}
                            />
                            <Link
                                component="button"
                                type="button"
                                underline="always"
                                onClick={initRigid.importFromFile}
                                disabled={isPropagating}
                                data-testid="replanning-init-rigid-import"
                                sx={{ fontSize: 11, mt: 0.5 }}
                            >
                                <FormattedMessage id="replanning.initRigid.import" />
                            </Link>
                            <input
                                type="file"
                                hidden
                                accept=".dcm,.dicom,.dc3,application/dicom"
                                data-testid="replanning-init-rigid-file"
                                {...initRigid.fileInputProps}
                            />
                        </Box>
                    )}
                </Box>
            )}

            <ContourModelSelect
                models={contourModels}
                value={task.comparisonModel}
                ranking={contourModelChoice.ranking}
                unavailable={contourModelChoice.unavailable}
                onChange={(choice) =>
                    setTask((current) => ({
                        ...current,
                        comparisonModel: choice,
                        comparisonModelChosen: true,
                    }))
                }
                disabled={isPropagating}
            />

            <Box flex={1} minHeight={0} display="flex" flexDirection="column">
                <RoiPlanTable
                    entries={entries}
                    colors={roiColors}
                    onIncludedChange={setIncluded}
                    onSourceChange={setSource}
                    disabled={isPropagating}
                />
            </Box>
        </Box>
    );
}
